/**
 * ModuleGuard — hides a route behind an effective-permission check.
 * If the user's effective permissions have the module flipped off, we
 * render a friendly Forbidden panel instead of the page. Main-hub users
 * always pass. If perms are still loading, we render children optimistically
 * (the backend will still enforce with 403s).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldOff, ArrowLeft } from 'lucide-react';
import { usePerms } from '../context/PermissionsContext';

export default function ModuleGuard({ moduleId, children }) {
  const { canAccessModule, loading } = usePerms();
  if (loading) return children;
  if (!canAccessModule(moduleId)) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid={`module-blocked-${moduleId}`}>
        <div className="max-w-md text-center p-8 border border-slate-700 rounded-lg bg-[#131A2A]">
          <ShieldOff className="w-14 h-14 mx-auto text-amber-400 mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Module Not Available</h2>
          <p className="text-slate-300 text-sm mb-4">
            Access to <span className="text-amber-300 font-mono">{moduleId.replace(/_/g, ' ')}</span> has been
            disabled for your account. Contact your Main Hub administrator to request access.
          </p>
          <Link to="/dashboard" className="inline-flex items-center gap-1 text-amber-400 hover:text-amber-300 text-sm">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }
  return children;
}
